import React from "react";
import { Link } from "react-router-dom";
import SocialLinks from "./SocialLinks";
import {
  articles,
  experiences,
  featuredProjects,
  socialLinks,
} from "../data";

const Home = () => {
  return (
    <section className="pb-8">
      <div className="grid gap-16 lg:grid-cols-[minmax(0,1.2fr)_20rem]">
        <div>
          <div className="max-w-3xl">
            <h1 className="font-display text-4xl tracking-tight text-stone-900 dark:text-stone-100 sm:text-5xl">
              Hey, I'm Obinna.
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-8 text-stone-600 dark:text-stone-300">
              I'm a senior full-stack and AI engineer from Nigeria. I design
              and ship AI-powered workflows, backend services, and AWS
              infrastructure with a bias for systems that are practical,
              maintainable, and measurable.
            </p>
            <div className="mt-8">
              <SocialLinks links={socialLinks} />
            </div>
          </div>

          <div className="mt-16 space-y-6">
            {featuredProjects.map((project) => (
              <article
                key={project.name}
                className="rounded-3xl border border-stone-200 bg-white p-6 shadow-[0_18px_50px_-24px_rgba(28,25,23,0.22)] dark:border-stone-800 dark:bg-stone-900 dark:shadow-[0_18px_50px_-24px_rgba(0,0,0,0.55)]"
              >
                <div className="flex items-start gap-4">
                  <div className="flex h-12 w-12 flex-none items-center justify-center rounded-2xl bg-stone-900 text-sm font-semibold text-stone-50 dark:bg-stone-100 dark:text-stone-900">
                    {project.accent}
                  </div>
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-3">
                      <h2 className="text-xl font-semibold text-stone-900 dark:text-stone-100">
                        {project.name}
                      </h2>
                      <span className="rounded-full bg-stone-100 px-3 py-1 text-xs font-medium uppercase tracking-[0.18em] text-stone-500 dark:bg-stone-800 dark:text-stone-300">
                        {project.meta}
                      </span>
                    </div>
                    <p className="mt-3 text-base leading-7 text-stone-600 dark:text-stone-300">
                      {project.summary}
                    </p>
                    <p className="mt-3 text-sm leading-7 text-stone-500 dark:text-stone-400">
                      {project.detail}
                    </p>
                    {project.href && (
                      <a
                        href={project.href}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-5 inline-flex items-center rounded-full border border-stone-200 px-4 py-2 text-sm font-medium text-stone-700 transition hover:border-stone-300 hover:text-stone-950 dark:border-stone-700 dark:text-stone-200 dark:hover:border-stone-600 dark:hover:text-stone-50"
                      >
                        View project
                      </a>
                    )}
                  </div>
                </div>
              </article>
            ))}
          </div>

          <div className="mt-10">
            <Link
              to="/projects"
              className="inline-flex items-center rounded-full bg-stone-900 px-5 py-3 text-sm font-medium text-stone-50 transition hover:bg-stone-700 dark:bg-stone-100 dark:text-stone-900 dark:hover:bg-stone-300"
            >
              View all projects
            </Link>
          </div>

          <section className="mt-20">
            <div className="max-w-3xl">
              <p className="text-sm font-semibold uppercase tracking-[0.24em] text-stone-500 dark:text-stone-400">
                Writing
              </p>
              <h2 className="font-display mt-4 text-3xl tracking-tight text-stone-900 dark:text-stone-100 sm:text-4xl">
                A couple of technical articles from my Medium archive.
              </h2>
              <p className="mt-4 text-base leading-8 text-stone-600 dark:text-stone-300">
                I have written about authentication flows in React Native and
                Firebase, with a focus on practical implementation rather than
                theory alone.
              </p>
            </div>

            <div className="mt-8 grid gap-6 sm:grid-cols-2">
              {articles.map((article) => (
                <article
                  key={article.title}
                  className="rounded-3xl border border-stone-200 bg-white p-6 shadow-[0_18px_50px_-24px_rgba(28,25,23,0.18)] dark:border-stone-800 dark:bg-stone-900 dark:shadow-[0_18px_50px_-24px_rgba(0,0,0,0.55)]"
                >
                  <p className="text-xs font-medium uppercase tracking-[0.2em] text-stone-500 dark:text-stone-400">
                    {article.meta}
                  </p>
                  <h3 className="mt-4 text-xl font-semibold text-stone-900 dark:text-stone-100">
                    {article.title}
                  </h3>
                  <p className="mt-3 text-sm leading-7 text-stone-600 dark:text-stone-300">
                    {article.summary}
                  </p>
                  <a
                    href={article.href}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-6 inline-flex items-center rounded-full border border-stone-200 px-4 py-2 text-sm font-medium text-stone-700 transition hover:border-stone-300 hover:text-stone-950 dark:border-stone-700 dark:text-stone-200 dark:hover:border-stone-600 dark:hover:text-stone-50"
                  >
                    Read article
                  </a>
                </article>
              ))}
            </div>

            <div className="mt-8">
              <a
                href="https://medium.com/@obinnaprime"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center rounded-full border border-stone-200 px-5 py-3 text-sm font-medium text-stone-700 transition hover:border-stone-300 hover:text-stone-950 dark:border-stone-700 dark:text-stone-200 dark:hover:border-stone-600 dark:hover:text-stone-50"
              >
                Visit Medium profile
              </a>
            </div>
          </section>
        </div>

        <aside className="space-y-6">
          <section className="rounded-3xl border border-stone-200 bg-white p-6 shadow-[0_18px_50px_-24px_rgba(28,25,23,0.18)] dark:border-stone-800 dark:bg-stone-900 dark:shadow-[0_18px_50px_-24px_rgba(0,0,0,0.55)]">
            <h2 className="text-sm font-semibold uppercase tracking-[0.22em] text-stone-500 dark:text-stone-400">
              Experience
            </h2>
            <ol className="mt-6 space-y-5">
              {experiences.map((item) => (
                <li key={item.company} className="flex gap-4">
                  <div className="mt-1 flex h-10 w-10 flex-none items-center justify-center rounded-full border border-stone-200 bg-stone-50 text-xs font-semibold uppercase tracking-[0.18em] text-stone-500 dark:border-stone-700 dark:bg-stone-800 dark:text-stone-300">
                    {item.company
                      .split(" ")
                      .map((part) => part[0])
                      .join("")
                      .slice(0, 2)}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-stone-900 dark:text-stone-100">
                      {item.company}
                    </p>
                    <p className="mt-1 text-sm text-stone-600 dark:text-stone-300">{item.role}</p>
                    <p className="mt-1 text-xs uppercase tracking-[0.18em] text-stone-400 dark:text-stone-500">
                      {item.period}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
          </section>

          <section className="rounded-3xl border border-stone-200 bg-stone-900 p-6 text-stone-50 shadow-[0_18px_50px_-24px_rgba(28,25,23,0.35)] dark:border-stone-700 dark:bg-stone-100 dark:text-stone-900">
            <h2 className="text-sm font-semibold uppercase tracking-[0.22em] text-stone-300 dark:text-stone-500">
              Focus
            </h2>
            <ul className="mt-5 space-y-3 text-sm leading-7 text-stone-200 dark:text-stone-700">
              <li>AI systems and LLM integrations</li>
              <li>Cloud-native platform engineering on AWS</li>
              <li>Deployment automation and observability</li>
              <li>Backend APIs and product-facing React applications</li>
            </ul>
          </section>
        </aside>
      </div>
    </section>
  );
};

export default Home;
